import moment, {Moment} from 'moment-timezone'

import type {CostAvoidanceMonthly, DatetimeValue} from '../api'

import {interpolateDatetimeValues} from './interpolateDatetimeValues'

export const getCostAvoidanceOptimizerUsage = (
  costAvoidanceMonthly: CostAvoidanceMonthly[],
  costAvoidancePerMonth: Record<string, DatetimeValue & {cumulatedValue: number}>,
  startOfToday: Moment
): DatetimeValue[] => {
  if (costAvoidanceMonthly.length === 0) {
    return []
  }

  const firstMonthDate: string = costAvoidanceMonthly[0].date
  const lastMonthDate: string = costAvoidanceMonthly[costAvoidanceMonthly.length - 1].date
  const start = moment.utc(firstMonthDate).startOf('month')
  const end = moment.min([
    moment.utc(lastMonthDate).endOf('month'),
    startOfToday.clone().add(1, 'day')
  ])

  // getting list of every day from start date to end date (excluding end)
  const allDaysInRange = getListOfDays({
    start,
    end
  })

  // getting optimizer usage dictionary only for days when it's used
  const daysOptimizerUsed = costAvoidanceMonthly.flatMap(({days}) => days)
  const daysOptimizerUsedDaily: Record<string, boolean> = Object.fromEntries(
    daysOptimizerUsed.map(({day, isOptimizerUsed}) => [
      moment.utc(day).startOf('day').toISOString(),
      isOptimizerUsed
    ])
  )

  // transforming costAvoidancePerMonth dictionary from left to right aligned (using end of month start of day key)
  const costAvoidancePerMonthRightAligned = Object.entries(costAvoidancePerMonth).reduce(
    (acc, [key, value]) => {
      const adjustedDate = moment.min([startOfToday, moment.utc(key).endOf('month').startOf('day')])
      acc[adjustedDate.toISOString()] = value
      return acc
    },
    {}
  )

  // creating the array of datetime values, if value doesn't exit it replace with null
  const everyDayValues: DatetimeValue<Moment, null | number>[] = allDaysInRange.map((datetime) => {
    const costAvoidance: undefined | (DatetimeValue & {cumulatedValue: number}) =
      costAvoidancePerMonthRightAligned[datetime.toISOString()]
    return {datetime: datetime, value: costAvoidance ? costAvoidance.cumulatedValue : null}
  })

  // replacing null values with interpolated value
  const allDaysDatetimeValuesInterpolated = interpolateDatetimeValues(everyDayValues)

  // creating datetime value array ready to use in chart
  const optimizerUsage: DatetimeValue[] = allDaysDatetimeValuesInterpolated.flatMap(
    ({datetime, value}, index) => {
      const datetimeKey = datetime.toISOString()
      const optimizerUsage: boolean = daysOptimizerUsedDaily[datetimeKey] ?? false
      const nextValue = allDaysDatetimeValuesInterpolated[index + 1]?.value ?? 0
      const startOfDayValue = optimizerUsage ? value : 0
      const endOfDayValue = optimizerUsage ? nextValue : 0
      return [
        {datetime: datetimeKey, value: startOfDayValue},
        {
          datetime: datetime.clone().endOf('day').toISOString(),
          value: endOfDayValue
        }
      ]
    }
  )
  return optimizerUsage
}

interface GetListOfDaysParams {
  start: Moment
  end?: Moment
  additionalDays?: number
}

const getListOfDays = ({start, end, additionalDays}: GetListOfDaysParams): Moment[] => {
  const retList: Moment[] = []
  retList.push(start)

  if (end !== undefined) {
    const current: Moment = start.clone().add(1, 'day')

    while (current < end) {
      retList.push(current.clone())
      current.add(1, 'days')
    }
  }

  if (additionalDays && additionalDays > 0) {
    const lastInList: Moment = retList[retList.length - 1]

    for (let i = 1; i <= additionalDays; i++) {
      retList.push(lastInList.clone().add(i, 'days'))
    }
  }
  return retList
}
